import { useEffect, useRef } from "react";

/**
 * PosternGate — the hero graphic. A stone wall with an arched gate that swings
 * open, warm light spilling through, ray-marched live and drawn as halftone dots.
 *
 * No video, no libraries: one canvas. Pauses when off-screen or in a background
 * tab, adapts dot size to the device's speed, and renders a single still frame
 * for visitors with prefers-reduced-motion. Colours come from the site's CSS
 * tokens (--faint for stone, --brass / --brass-dark for the light).
 */

const TAU = Math.PI * 2;
const ease = (x) => (x < 0 ? 0 : x > 1 ? 1 : x * x * (3 - 2 * x));

function open2d(x, y) {
  const qx = Math.abs(x) - 0.8, qy = Math.abs(y - 1.1) - 1.1;
  const dr = Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) + Math.min(Math.max(qx, qy), 0);
  const dc = Math.hypot(x, y - 2.2) - 0.8;
  return Math.min(dr, dc);
}

function makeScene() {
  const s = { c: 1, s: 0, mat: 0 };
  s.map = (px, py, pz) => {
    let d = py; s.mat = 0;
    const qx = Math.abs(px) - 30, qy = Math.abs(py - 2.6) - 2.6, qz = Math.abs(pz) - 0.22;
    const wb = Math.hypot(Math.max(qx, 0), Math.max(qy, 0), Math.max(qz, 0)) + Math.min(Math.max(qx, qy, qz), 0);
    const wall = Math.max(wb, -open2d(px, py));
    if (wall < d) { d = wall; s.mat = 1; }
    const dx = px + 0.8, dz = pz - 0.16;
    const lx = dx * s.c + dz * s.s, lz = -dx * s.s + dz * s.c;
    const leaf = Math.max(open2d(lx - 0.8, py) + 0.03, Math.abs(lz) - 0.05);
    if (leaf < d) { d = leaf; s.mat = 2; }
    return d;
  };
  return s;
}

const LX = 0.2, LY = 1.7, LZ = 3.4;
const CYCLE = 18;
const GRID_DIM = 0.65;   // 0 = background grid stays as is; 0.65 = it dims while the pattern appears
const FLOOR = 0.08;   // brightness of the dotted floor beyond the wall
const RECOG = 7.4, STEP = 0.28; // when the pattern starts revealing, and how fast it spreads
// whitespace field: a regular grid of ground points behind the wall; ~1 in 7 is an opportunity
const FIELD = (() => {
  const out = [];
  let seed = 4242;
  const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
  for (let z = 1.2; z < 34; z += 0.8) for (let x = -16; x <= 16; x += 0.8) out.push(x, z, rnd() < 0.12 ? 1 : 0, (rnd() - 0.5) * 1.1, (rnd() - 0.5) * 1.1);
  return out;
})();
const NET = (() => {
  const ids = [];
  for (let k = 0; k < FIELD.length; k += 5) if (FIELD[k + 2]) ids.push(k);
  const edges = [], adj = new Map(ids.map((k) => [k, []]));
  const seen = new Set();
  for (const a of ids) {
    const near = ids.filter((b) => b !== a)
      .map((b) => [b, Math.hypot(FIELD[a] - FIELD[b], FIELD[a + 1] - FIELD[b + 1])])
      .filter(([, d]) => d < 3.2).sort((x, y) => x[1] - y[1]).slice(0, 2);
    for (const [b] of near) {
      const key = a < b ? a + ":" + b : b + ":" + a;
      if (seen.has(key)) continue;
      seen.add(key); edges.push([a, b, 0]); adj.get(a).push(b); adj.get(b).push(a);
    }
  }
  // reveal order: a ripple spreading out from the point straight ahead
  const level = new Map(ids.map((k) => [k, Math.floor(Math.hypot(FIELD[k], (FIELD[k + 1] - 5) * 0.8) / 1.4)]));
  for (const e of edges) e[2] = Math.max(level.get(e[0]), level.get(e[1]));
  return { edges, level };
})();

function dots(ctx, list, color) {
  if (!list.length) return;
  ctx.fillStyle = color;
  ctx.beginPath();
  for (let i = 0; i < list.length; i += 3) {
    ctx.moveTo(list[i] + list[i + 2], list[i + 1]);
    ctx.arc(list[i], list[i + 1], list[i + 2], 0, TAU);
  }
  ctx.fill();
}

export default function PosternGate({ className = "", labels = [] }) {
  const ref = useRef(null);
  const labelsRef = useRef(labels);
  labelsRef.current = labels;

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return undefined;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const cs = getComputedStyle(document.documentElement);
    const tok = (n, f) => cs.getPropertyValue(n).trim() || f;
    const col = { stone: tok("--ink-2", "#3d3934"), brass: tok("--brass", "#eaa221"), edge: tok("--brass-dark", "#96690d"), ink: tok("--ink", "#1a1815"), bg: tok("--bg", "#f7f6f3"), line: tok("--line-2", "#d6cfbf") };
    const sans = tok("--sans", "\"IBM Plex Sans\", sans-serif");
    let pinned = null; // field points that carry the headlines, chosen once on arrival

    const scene = makeScene();
    let step = 8, slow = 0, fast = 0, raf = 0, last = 0, visible = true;
    let mx = 0, my = 0, tx = 0, ty = 0, t0 = -1;

    const onMove = (e) => {
      const r = cv.getBoundingClientRect();
      tx = Math.max(-1, Math.min(1, ((e.clientX - r.left) / r.width) * 2 - 1));
      ty = Math.max(-1, Math.min(1, ((e.clientY - r.top) / r.height) * 2 - 1));
    };
    const onLeave = () => { tx = 0; ty = 0; };

    function draw(t) {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = cv.getBoundingClientRect();
      const w = Math.max(1, Math.round(rect.width)), h = Math.max(1, Math.round(rect.height));
      if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(h * dpr)) { cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr); }
      const ctx = cv.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      mx += (tx - mx) * 0.06; my += (ty - my) * 0.06;
      // One journey per loop: the door opens (never closes), we walk through,
      // and a field of whitespace opens up. The loop then fades out and the
      // door, closed again off-screen, opens afresh.
      if (t0 < 0) t0 = t;
      const u = (t - t0) % CYCLE;
      const a = 1.5 * ease((u - 0.5) / 2.5);
      scene.c = Math.cos(a); scene.s = Math.sin(a);
      const walk = ease((u - 3.6) / 3.6);                       // 0 outside → 1 inside
      const fade = Math.min(ease(u / 0.6), 1 - ease((u - (CYCLE - 1.1)) / 1.0));
      const creep = Math.max(0, u - 7.2) * 0.07;                  // slow drift once inside

      const yaw = (0.26 * Math.sin(t * 0.17) + mx * 0.35) * (1 - walk), R = 6.9;
      const ox = Math.sin(yaw) * R, oy = 2.3 - my * 0.7, oz = -Math.cos(yaw) * R;
      const rox = ox * (1 - walk), roy = oy + (1.15 - oy) * walk, roz = oz + (2.6 + creep - oz) * walk;
      const gx = mx * 2.5 * walk, gy = 1.55 + (0.55 - 1.55) * walk - my * 0.8 * walk, gz = 12 * walk + creep;
      let fx = gx - rox, fy = gy - roy, fz = gz - roz; const fl = Math.hypot(fx, fy, fz); fx /= fl; fy /= fl; fz /= fl;
      let rx = fz, rz = -fx; const rl = Math.hypot(rx, rz); rx /= rl; rz /= rl;
      const uX = fy * rz, uY = fz * rx - fx * rz, uZ = -fy * rx;

      const S = step, cols = Math.ceil(w / S), rows = Math.ceil(h / S);
      const stone = [], brass = [], hot = [], door = [], rim = [];
      const tStart = performance.now();
      const map = scene.map;
      for (let j = 0; j < rows; j++) {
        const cy = (j + 0.5) * S, sy = (h / 2 - cy) / h;
        for (let i = 0; i < cols; i++) {
          const cx = (i + 0.5) * S, sx = (cx - w / 2) / h;
          // soft oval vignette so the graphic melts into the page
          const vx = (cx / w - 0.5) * 2, vy = (cy / h - 0.5) * 2;
          const vig = 1 - ease((Math.pow(Math.pow(Math.abs(vx), 4) + Math.pow(Math.abs(vy) * 1.05, 4), 0.25) - 0.62) / 0.38);
          if (vig <= 0.01) continue;
          let dx = fx * 1.5 + rx * sx + uX * sy, dy = fy * 1.5 + uY * sy, dz = fz * 1.5 + rz * sx + uZ * sy;
          const dl = Math.hypot(dx, dy, dz); dx /= dl; dy /= dl; dz /= dl;
          let tt = 0, hit = false, m = 0;
          for (let k = 0; k < 72 && tt < 40; k++) {
            const d = map(rox + dx * tt, roy + dy * tt, roz + dz * tt);
            if (d < 0.0015 * tt + 0.001) { hit = true; m = scene.mat; break; }
            tt += d * 0.95;
          }
          let warm = 0, cool = 0, part = 0; // 1 = door leaf, 2 = arch rim
          const lpx = LX - rox, lpy = LY - roy, lpz = LZ - roz;
          const tc = lpx * dx + lpy * dy + lpz * dz;
          if (!hit && dz > 0) warm += 0.32 * Math.exp(-Math.abs(dy) * 80);
          // soft, even wash behind the gate instead of a visible light orb
          // (an orb read as a head peering round the door)
          if (roz < -0.3 && tc > 0 && (!hit || tt > tc) && dz > 0) warm += 0.18;
          if (hit) {
            const px = rox + dx * tt, py = roy + dy * tt, pz = roz + dz * tt, e = 0.002;
            if (m === 2) part = 1;
            let nx = map(px + e, py, pz) - map(px - e, py, pz);
            let ny = map(px, py + e, pz) - map(px, py - e, pz);
            let nz = map(px, py, pz + e) - map(px, py, pz - e);
            const nl = Math.hypot(nx, ny, nz) || 1; nx /= nl; ny /= nl; nz /= nl;
            let alb;
            if (m === 1) {
              const by = py / 0.42, row = Math.floor(by), fyv = by - row;
              const bx = px / 0.9 + (row % 2) * 0.5, fxv = bx - Math.floor(bx);
              alb = Math.min(fyv, 1 - fyv) < 0.07 || Math.min(fxv, 1 - fxv) < 0.035 ? 0.12 : 0.95;
            } else if (m === 2) {
              alb = Math.abs(((px + 0.8) * 3.0) % 1) < 0.06 ? 0.45 : 0.75;
            } else {
              alb = pz > 0.3 ? FLOOR : 0.75 * Math.max(0, 1 - Math.hypot(px, pz) / 11);
            }
            let lx = LX - px, ly = LY - py, lz = LZ - pz; const ld = Math.hypot(lx, ly, lz); lx /= ld; ly /= ld; lz /= ld;
            const ndl = nx * lx + ny * ly + nz * lz;
            if (ndl > 0) {
              let sh = 1, st = 0.04;
              const ox = px + nx * 0.01, oy = py + ny * 0.01, oz = pz + nz * 0.01;
              for (let k = 0; k < 32 && st < ld; k++) {
                const hd = map(ox + lx * st, oy + ly * st, oz + lz * st);
                if (hd < 0.001) { sh = 0; break; }
                sh = Math.min(sh, (10 * hd) / st);
                st += Math.max(hd, 0.03);
              }
              warm += (alb * ndl * sh * 5.2) / (1 + 0.32 * ld * ld);
            }
            cool += alb * (0.07 + 0.42 * Math.max(0, nx * -0.35 + ny * 0.75 + nz * -0.56));
          }
          const I = Math.min(1, warm + cool) * vig;
          if (part === 1) { door.push(cx, cy, S * 0.5 * Math.max(0.5, Math.sqrt(Math.min(1, cool * 1.6 + 0.25))) * vig); continue; }
          if (part === 2) { rim.push(cx, cy, S * 0.42 * vig); continue; }
          if (I < 0.02) continue;
          const r = S * 0.5 * Math.sqrt(I) * 0.96;
          if (r < 0.35) continue;
          if (warm > 0.1 && warm > cool * 0.9) (warm > 0.35 && warm < 0.6 ? hot : brass).push(cx, cy, r);
          else stone.push(cx, cy, r);
        }
      }
      ctx.globalAlpha = 0.42 * fade; dots(ctx, stone, col.stone);
      ctx.globalAlpha = fade; dots(ctx, brass, col.brass); dots(ctx, hot, col.edge);
      ctx.globalAlpha = 0.9 * fade; dots(ctx, door, col.stone);
      ctx.globalAlpha = fade; dots(ctx, rim, col.edge);

      // the whitespace field: points on the ground beyond the wall. Brass ones
      // are opportunities. Seen only through the opening until we step inside.
      const faint = [], opp = [];
      const scr = new Map(), hotOpp = [];
      const inside = roz > 0.25;
      // scattered → ordered: the noise falls away and the grid underneath appears
      const order = ease((u - RECOG) / 3.2);
      for (let k = 0; k < FIELD.length; k += 5) {
        const px = FIELD[k] + FIELD[k + 3] * (1 - order), pz = FIELD[k + 1] + FIELD[k + 4] * (1 - order), isOpp = FIELD[k + 2];
        const py = 0.02;
        const vx = px - rox, vy = py - roy, vz = pz - roz;
        const Z = vx * fx + vy * fy + vz * fz;
        if (Z < 0.3) continue;
        if (!inside) {
          const tt = (0 - roz) / (pz - roz);
          if (tt <= 0 || tt >= 1 || open2d(rox + (px - rox) * tt, roy + (py - roy) * tt) > -0.04) continue;
          // hidden behind the door leaf? (2D test in the ground plane: sight line vs the leaf)
          const hx = -0.8, hz = 0.16, ex = hx + 1.6 * scene.c, ez = hz + 1.6 * scene.s;
          const d1x = px - rox, d1z = pz - roz, d2x = ex - hx, d2z = ez - hz;
          const den = d1x * d2z - d1z * d2x;
          if (Math.abs(den) > 1e-6) {
            const s1 = ((hx - rox) * d2z - (hz - roz) * d2x) / den, s2 = ((hx - rox) * d1z - (hz - roz) * d1x) / den;
            if (s1 > 0 && s1 < 1 && s2 > 0 && s2 < 1) continue;
          }
        }
        const X = vx * rx + vz * rz, Y = vx * uX + vy * uY + vz * uZ;
        const cx = w / 2 + (X * 1.5 / Z) * h, cy = h / 2 - (Y * 1.5 / Z) * h;
        if (cx < 0 || cx > w || cy < 0 || cy > h) continue;
        const vxn = (cx / w - 0.5) * 2, vyn = (cy / h - 0.5) * 2;
        const vig = 1 - ease((Math.pow(Math.pow(Math.abs(vxn), 4) + Math.pow(Math.abs(vyn) * 1.05, 4), 0.25) - 0.62) / 0.38);
        const far = Math.max(0, 1 - Z / 34);
        if (vig * far <= 0.02) continue;
        if (isOpp) {
          const pulse = 0.75 + 0.25 * Math.sin(t * 2.2 + px * 1.7 + pz * 0.9);
          const r = Math.min(8, (0.09 * 1.5 / Z) * h) * pulse * Math.sqrt(vig * far);
          if (r > 0.4) {
            const lv = NET.level.get(k);
            const lit = lv !== undefined && u > RECOG + lv * STEP;   // already part of the pattern
            const rr = lit ? r * (1.15 + 0.2 * Math.sin(t * 3 - lv * 0.5)) : r;
            (lit ? hotOpp : opp).push(cx, cy, rr);
            scr.set(k, [cx, cy, Math.sqrt(vig * far)]);
          }
        } else {
          const r = Math.min(3.2, (0.03 * 1.5 / Z) * h) * Math.sqrt(vig * far);
          if (r > 0.35) faint.push(cx, cy, r);
        }
      }
      ctx.globalAlpha = 0.5 * fade * (1 - GRID_DIM * order); dots(ctx, faint, col.stone);
      // recognition: links between opportunities draw themselves, spreading out
      // from the point straight ahead, until the hidden pattern is all visible
      if (u > RECOG) {
        ctx.strokeStyle = col.brass; ctx.lineWidth = 1.6; ctx.lineCap = "round";
        for (const [a, b, lv] of NET.edges) {
          const pa = scr.get(a), pb = scr.get(b);
          if (!pa || !pb) continue;
          const p = ease((u - RECOG - lv * STEP) / 0.6);
          if (p <= 0) continue;
          const glow = 0.55 + 0.45 * Math.sin(t * 3 - lv * 0.5);
          ctx.globalAlpha = fade * Math.sqrt(Math.min(pa[2], pb[2])) * (0.55 + 0.4 * glow);
          ctx.beginPath(); ctx.moveTo(pa[0], pa[1]); ctx.lineTo(pa[0] + (pb[0] - pa[0]) * p, pa[1] + (pb[1] - pa[1]) * p); ctx.stroke();
        }
      }
      ctx.globalAlpha = fade; dots(ctx, opp, col.brass); dots(ctx, hotOpp, col.edge);

      // name the pattern: each headline pins itself to a brass point, one after another
      const labels = labelsRef.current;
      if (labels.length && u > RECOG + 0.6 && scr.size) {
        if (!pinned) {
          const spots = w < 520
            ? [[0.2, 0.55], [0.42, 0.74], [0.62, 0.55], [0.8, 0.74]] // phones: staggered so tags don't overlap
            : [[0.2, 0.56], [0.4, 0.53], [0.6, 0.53], [0.8, 0.56]]; // one even row, equal weight
          const used = new Set();
          pinned = spots.slice(0, labels.length).map(([ax, ay]) => {
            let best = null, bd = 1e9;
            for (const [k, [px, py]] of scr) {
              if (used.has(k)) continue;
              const d = Math.hypot(px / w - ax, (py / h - ay) * 1.4);
              if (d < bd) { bd = d; best = k; }
            }
            used.add(best);
            return best;
          });
        }
        const fs = w < 420 ? 10.5 : 12.5;
        ctx.font = `600 ${fs}px ${sans}`; ctx.textBaseline = "middle"; ctx.textAlign = "left";
        labels.forEach((text, i) => {
          const pos = scr.get(pinned[i]);
          if (!pos) return;
          const a = ease((u - RECOG - 0.6 - i * 0.25) / 0.5) * fade;
          if (a <= 0) return;
          const [px, py] = pos;
          // ring on the point
          ctx.globalAlpha = a; ctx.strokeStyle = col.edge; ctx.lineWidth = 1.5;
          ctx.beginPath(); ctx.arc(px, py, 7 + 2 * Math.sin(t * 3 + i), 0, Math.PI * 2); ctx.stroke();
          // leader up to the label
          const lift = 10, tw = ctx.measureText(text).width, padX = 7, bh = fs + 8;
          let bx = px - tw / 2 - padX; bx = Math.max(6, Math.min(w - tw - padX * 2 - 6, bx));
          const by = py - lift - bh;
          ctx.strokeStyle = col.line; ctx.lineWidth = 1;
          ctx.beginPath(); ctx.moveTo(px, py - 9); ctx.lineTo(px, by + bh); ctx.stroke();
          ctx.fillStyle = col.bg; ctx.globalAlpha = 0.92 * a;
          ctx.beginPath(); ctx.roundRect ? ctx.roundRect(bx, by, tw + padX * 2, bh, bh / 2) : ctx.rect(bx, by, tw + padX * 2, bh); ctx.fill();
          ctx.globalAlpha = a; ctx.strokeStyle = col.edge; ctx.lineWidth = 1.2; ctx.stroke();
          ctx.fillStyle = col.ink; ctx.fillText(text, bx + padX, by + bh / 2 + 0.5);
        });
      }
      ctx.globalAlpha = 1;

      const ms = performance.now() - tStart;
      if (ms > 22) { if (++slow > 6 && step < 13) { step++; slow = 0; } } else slow = 0;
      if (ms < 9) { if (++fast > 40 && step > 7) { step--; fast = 0; } } else fast = 0;
    }

    if (reduce) {
      t0 = 0; draw(5);
      const onResize = () => draw(5);
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }

    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; }, { rootMargin: "60px" });
    io.observe(cv);
    window.addEventListener("pointermove", onMove, { passive: true });
    document.documentElement.addEventListener("pointerleave", onLeave);
    const loop = (now) => {
      if (visible && !document.hidden && now - last > 30) { last = now; draw(now / 1000); }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener("pointermove", onMove);
      document.documentElement.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return <canvas ref={ref} className={`postern-gate ${className}`} aria-hidden="true" />;
}
